import React, { createContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { Loader, NetlifySync } from 'components';
import MaintenanceBreak from 'components/MaintenanceBreak';
import { useAutoScroll, useRedirect } from '../utils/hooks';
import { isSSRTheme, localStore } from 'utils';
import { STORAGE_KEYS } from 'utils/DICTIONARY';
import rrFavicon from 'assets/img/brand/rr-favicon.svg';
import trFavicon from 'assets/img/brand/tr-favicon.svg';
import ssFavicon from 'assets/img/brand/ss-favicon.png';

export const AppContext = createContext({
  isOnline: true,
  isSSRMode: false,
  author: 'Return Rabbit',
  appName: 'Return Rabbit',
  supportEmail: 'support@returnrabbit.com'
});

export function AppProvider({ children = null }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isRedirecting = useRedirect();
  useAutoScroll();

  const isTrackingApp = process.env.REACT_APP_TYPE === 'SHIPMENT_TRACKING';
  const isSSRMode = isSSRTheme();
  const author = isSSRMode ? 'ShipStation' : 'Return Rabbit';
  const appName = isTrackingApp
    ? 'Tracking Rabbit'
    : isSSRMode
    ? 'ShipStation Returns'
    : 'Return Rabbit';
  const appFavicon = isTrackingApp
    ? trFavicon
    : isSSRMode
    ? ssFavicon
    : rrFavicon;
  const fontClass = 'Roboto-Regular';
  // const fontClass = isSSRMode ? 'Roboto-Regular' : 'AtlasGrotesk-Regular-Web';
  const supportEmail = isSSRMode
    ? 'returns@shipstation.com'
    : 'support@returnrabbit.com';

  useEffect(() => {
    document.body.classList.add(fontClass);
    const storageFunction = ({ key }) => {
      if (key === 'userData') {
        window.location.reload();
      } else if (key === STORAGE_KEYS.LOGGED_IN_STORE_NAME) {
        window.location.href = '/';
      }
    };

    const clearTempStorageData = () => {
      setTimeout(
        () => localStore.clear(STORAGE_KEYS.POST_REFRESH_TOKEN_GENERATE_RELOAD),
        10_000
      );
    };

    window.addEventListener('online', () => setIsOnline(true));
    window.addEventListener('offline', () => setIsOnline(false));
    window.addEventListener('storage', storageFunction);
    window.addEventListener('load', clearTempStorageData);

    return () => {
      document.body.classList.remove(fontClass);
      window.removeEventListener('online', () => setIsOnline(true));
      window.removeEventListener('offline', () => setIsOnline(false));
      window.removeEventListener('storage', storageFunction);
      window.removeEventListener('load', clearTempStorageData);
    };
  }, [fontClass]);

  return (
    <AppContext.Provider
      value={{ isOnline, isSSRMode, author, appName, supportEmail }}
    >
      <Helmet>
        <title>{appName}</title>
        <link rel="shortcut icon" href={appFavicon} />
      </Helmet>
      <NetlifySync isEnabled={!isRedirecting} />
      <div className="app">
        <Loader loading={isRedirecting}>
          <MaintenanceBreak>{children}</MaintenanceBreak>
        </Loader>
      </div>
    </AppContext.Provider>
  );
}
